import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Board } from "../lib/api/boards";
import BoardContextMenu from "./BoardContextMenu";
import { motion } from "framer-motion";

interface BoardCardProps {
  board: Board;
  onRename: (id: string, newName: string) => void;
  onDelete: (id: string) => void;
  index?: number;
}

const BoardCard = ({
  board,
  onRename,
  onDelete,
  index = 0,
}: BoardCardProps) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/boards/${board.id}`, { state: { boardName: board.name } });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return t("time.today", "Today");
    if (diffDays === 2) return t("time.yesterday", "Yesterday");
    if (diffDays <= 7) return t("time.daysAgo", { count: diffDays - 1 });
    return date.toLocaleDateString();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      onClick={handleClick}
      className="group relative bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 p-4 hover:shadow-md hover:border-accent-primary/30 transition-all cursor-pointer overflow-hidden"
    >
      {/* Card Header/Icon Area */}
      <div className="flex items-start justify-between mb-4">
        <div className="w-10 h-10 bg-neutral-100 dark:bg-neutral-800 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
          <span className="w-5 h-5 text-accent-primary" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_boards_lamp.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_boards_lamp.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
        </div>
        
        <div onClick={(e) => e.stopPropagation()}>
          <BoardContextMenu
            boardId={board.id}
            boardName={board.name}
            onRename={onRename}
            onDelete={onDelete}
          />
        </div>
      </div>

      {/* Board Info */}
      <div className="space-y-1">
        <h3 className="text-sm font-semibold text-text-primary dark:text-neutral-200 truncate group-hover:text-accent-primary transition-colors">
          {board.name}
        </h3>
        <div className="flex items-center gap-2 text-[10px] text-neutral-500 dark:text-neutral-400">
          <span>{formatDate(board.updatedAt)}</span>
          <span>•</span>
          <span className="truncate">{t("boards.you", "You")}</span>
        </div>
      </div>

      {/* Decorative background element */}
      <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-accent-primary/5 rounded-full blur-2xl group-hover:bg-accent-primary/10 transition-colors pointer-events-none" />
    </motion.div>
  );
};

export default BoardCard;
